// ============================================================================
// lib/checkout/products.ts — catalogue de products (métadonnées côté client).
// ============================================================================
// Les IDs correspondent aux products créés dans App Store Connect (et Google
// Play Console à terme). Les prix / titres affichés proviennent en priorité
// du store (via `Purchases.getOfferings()` sur RC), avec ce catalogue comme
// FALLBACK — utile en dev / si l'offering RC est en cours de config.
//
// Miroir Stripe (cf. `packages/db/migrations/0040_access_passes.sql` +
// `apps/app/src/components/exercise-paywall-card.tsx`).
// ============================================================================

import type { CheckoutProduct, CheckoutProductId } from './types';

export const CHECKOUT_PRODUCTS: Record<CheckoutProductId, CheckoutProduct> = {
  // ── Passes Normal ─────────────────────────────────────────────────────────
  jeraisonne_pass_normal_24h: {
    id: 'jeraisonne_pass_normal_24h',
    title: 'Passe exercices 24h',
    priceLabel: '1,99 €',
    description: 'Accès unique de 24h à tous les exercices — sans engagement.',
    kind: 'consumable',
    category: 'normal',
    durationLabel: '24h',
  },
  jeraisonne_pass_normal_7d: {
    id: 'jeraisonne_pass_normal_7d',
    title: 'Abonnement 7 jours',
    priceLabel: '3,99 € / 7 jours',
    description: 'Renouvellement automatique tous les 7 jours, résiliable.',
    kind: 'auto_renew_subscription',
    category: 'normal',
    durationLabel: '7 jours',
  },
  jeraisonne_pass_normal_30d: {
    id: 'jeraisonne_pass_normal_30d',
    title: 'Abonnement 30 jours',
    priceLabel: '9,99 € / mois',
    description: 'Renouvellement automatique mensuel, résiliable.',
    kind: 'auto_renew_subscription',
    category: 'normal',
    durationLabel: '30 jours',
  },

  // ── Passes Épreuves (Brevet, Bac) ─────────────────────────────────────────
  jeraisonne_pass_epreuves_24h: {
    id: 'jeraisonne_pass_epreuves_24h',
    title: 'Passe épreuves 24h',
    priceLabel: '2,99 €',
    description: 'Accès unique de 24h aux annales Brevet et Bac — sans engagement.',
    kind: 'consumable',
    category: 'epreuves',
    durationLabel: '24h',
  },
  jeraisonne_pass_epreuves_7d: {
    id: 'jeraisonne_pass_epreuves_7d',
    title: 'Épreuves 7 jours',
    priceLabel: '5,99 € / 7 jours',
    description: 'Accès annales Brevet et Bac, renouvellement automatique 7j.',
    kind: 'auto_renew_subscription',
    category: 'epreuves',
    durationLabel: '7 jours',
  },
  jeraisonne_pass_epreuves_30d: {
    id: 'jeraisonne_pass_epreuves_30d',
    title: 'Épreuves 30 jours',
    priceLabel: '15,99 € / mois',
    description: 'Accès annales Brevet et Bac, renouvellement mensuel.',
    kind: 'auto_renew_subscription',
    category: 'epreuves',
    durationLabel: '30 jours',
  },

  // ── Abonnements Mensuels ──────────────────────────────────────────────────
  jeraisonne_student_monthly: {
    id: 'jeraisonne_student_monthly',
    title: 'Abonnement mensuel',
    priceLabel: '9,99 € / mois',
    description: 'Accès à tous les exercices, résiliable à tout moment.',
    kind: 'auto_renew_subscription',
    category: 'subscription',
    durationLabel: '1 mois',
  },
  jeraisonne_family_monthly: {
    id: 'jeraisonne_family_monthly',
    title: 'Abonnement famille',
    priceLabel: '29,99 € / mois',
    description: "Jusqu'à 3 enfants sous un seul abonnement.",
    kind: 'auto_renew_subscription',
    category: 'subscription',
    durationLabel: '1 mois',
  },

  // ── Abonnement Prof (migration 0691) ──────────────────────────────────────
  jeraisonne_tutor_monthly: {
    id: 'jeraisonne_tutor_monthly',
    title: 'Abonnement Prof',
    priceLabel: '29,90 € / mois',
    description: 'Tout l’espace prof, résiliable à tout moment.',
    kind: 'auto_renew_subscription',
    category: 'tutor',
    durationLabel: '1 mois',
  },
};

export function getProductById(id: CheckoutProductId): CheckoutProduct {
  return CHECKOUT_PRODUCTS[id];
}

/** Retourne tous les products d'une catégorie donnée (pour l'UI de sélection). */
export function getProductsByCategory(category: CheckoutProduct['category']): CheckoutProduct[] {
  return Object.values(CHECKOUT_PRODUCTS).filter((p) => p.category === category);
}
